// Making React Context FAST!
// Jack Herrington
// https://www.youtube.com/watch?v=ZKlXqrcBx88

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export default function createFastContext<Store>(initialState: Store) {
  function useStoreData(): {
    get: () => Store;
    set: (value: Partial<Store>) => void;
    subscribe: (callback: () => void) => () => void;
  } {
    // Holds current state without triggering re-renders on every update
    const store = useRef(initialState);

    // Set gives O(1) add/delete; avoids duplicates when a component subscribes multiple times
    const subscribers = useRef(new Set<() => void>());

    // Returns current state
    const get = useCallback(() => store.current, []);

    // Sets (part of) state
    // Calls all subscribers
    const set = useCallback((value: Partial<Store>) => {
      store.current = { ...store.current, ...value };
      subscribers.current.forEach((callback) => callback());
    }, []);

    // Returns an unsubscribe function so useEffect cleanup can remove the callback automatically
    const subscribe = useCallback((callback: () => void) => {
      subscribers.current.add(callback);
      return () => subscribers.current.delete(callback);
    }, []);

    return {
      get,
      set,
      subscribe,
    };
  }

  type UseStoreDataType = ReturnType<typeof useStoreData>;

  const StoreContext = createContext<UseStoreDataType | null>(null);

  function useStore<SelectorOutput>(
    selector: (store: Store) => SelectorOutput,
  ): [SelectorOutput, (value: Partial<Store>) => void] {
    const store = useContext(StoreContext);

    if (!store) {
      throw Error('No store found.');
    }

    const [state, setState] = useState(selector(store.get()));

    // Re-renders this component only when the selected slice of the store actually changes
    useEffect(() => {
      return store.subscribe(() => setState(selector(store.get())));
    }, [selector, store]);

    return [state, store.set];
  }

  function StoreProvider({ children }: { children: ReactNode }) {
    return (
      <StoreContext.Provider value={useStoreData()}>
        {children}
      </StoreContext.Provider>
    );
  }

  return {
    useStore,
    StoreProvider,
  };
}
